import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  Users,
  Activity,
  DollarSign,
  TrendingUp,
  ShieldAlert,
  RefreshCw,
  Sliders,
  Eye,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import MAMVolumeChart from '../components/dashboard/MAMVolumeChart';
import MAMPerformanceChart from '../components/dashboard/MAMPerformanceChart';
import MAMRiskMeter from '../components/dashboard/MAMRiskMeter';
import {
  mamDashboardService,
  mamPerformanceService,
} from '../services/mamService';
import { formatCurrency, formatPercent } from '../utils';
import { MAM_ROUTES } from '../../../constants/routeConstants';
import type { MAMDashboardKPIs, MAMPerformancePoint } from '../types';

export default function MAMDashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<MAMDashboardKPIs | null>(null);
  const [perfData, setPerfData] = useState<MAMPerformancePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpiRes, perfRes] = await Promise.all([
        mamDashboardService.getKPIs(),
        mamPerformanceService.getSeries(),
      ]);
      setKpis(kpiRes);
      setPerfData(perfRes);
    } catch {
      setError('Failed to load MAM Dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Dashboard" />
        <LoadingSkeleton type="cards" cols={4} />
        <LoadingSkeleton type="table" rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Dashboard" />
        <ErrorState message={error} onRetry={loadData} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Dashboard"
        description="Multi-Account Manager allocation, trading engine & portfolio overview"
      >
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </PageHeader>

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatsCard
          title="Trade Volume"
          value={`${kpis?.totalVolumeLots.toLocaleString()} Lots`}
          change={formatPercent(kpis?.totalVolumeChange || 0)}
          icon={Layers}
        />
        <StatsCard
          title="Client Accounts"
          value={kpis?.totalClients || 0}
          change={formatPercent(kpis?.totalClientsChange || 0)}
          icon={Users}
        />
        <StatsCard
          title="Open Trades"
          value={kpis?.openTradesCount || 0}
          change={formatPercent(kpis?.openTradesChange || 0)}
          icon={Activity}
        />
        <StatsCard
          title="Total Profit"
          value={formatCurrency(kpis?.totalProfit || 0, 'USD', true)}
          change={formatPercent(kpis?.totalProfitChange || 0)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Commission"
          value={formatCurrency(kpis?.totalCommission || 0, 'USD', true)}
          change={formatPercent(kpis?.totalCommissionChange || 0)}
          icon={DollarSign}
        />
        <StatsCard
          title="Avg Win Rate"
          value={formatPercent(kpis?.avgWinRate || 0, false)}
          icon={ShieldAlert}
        />
      </div>

      {/* Risk Index & Quick Actions Bar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
        <div className="lg:col-span-8">
          <MAMRiskMeter score={kpis?.riskScore || 28} />
        </div>
        <div className="lg:col-span-4 flex flex-col sm:flex-row items-center gap-2">
          <Button
            variant="outline"
            className="flex-1 text-xs"
            onClick={() => navigate(MAM_ROUTES.MAM_ALLOCATION)}
          >
            <Sliders className="w-3.5 h-3.5 mr-1.5 text-indigo-600" />
            Allocation Pool
          </Button>
          <Button
            variant="outline"
            className="flex-1 text-xs"
            onClick={() => navigate(MAM_ROUTES.MAM_TRADES)}
          >
            <Eye className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
            Trade Monitor
          </Button>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MAMPerformanceChart data={perfData} />
        <MAMVolumeChart data={perfData} />
      </div>
    </div>
  );
}
